import { useEffect, useState } from "react";
import notify from "devextreme/ui/notify";
import { useTranslation } from "react-i18next";
import {
    Integration, ApiKey, INTEGRATIONS, INTEGRATION_CATEGORIES, MOCK_API_KEYS, mockLogs,
} from "./integration-data";

export default function IntegrationsPage() {
    const { t } = useTranslation();
    const [items, setItems] = useState<Integration[]>(() => JSON.parse(JSON.stringify(INTEGRATIONS)));
    const [configOf, setConfigOf] = useState<Integration | null>(null);
    const [logsOf, setLogsOf] = useState<Integration | null>(null);

    const setStatus = (id: string, status: Integration["status"]) =>
        setItems(its => its.map(i => i.id === id ? { ...i, status } : i));

    const test = (i: Integration) => {
        if (i.status !== "connected") { notify(`${i.name}: önce bağlanın`, "warning", 2000); return; }
        notify(`${i.name}: bağlantı testi başarılı ✓`, "success", 2000);
    };

    return (
        <div className="h-full w-full" style={{ overflowY: "auto", padding: 20, color: "var(--base-text-color)" }}>
            <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>{t("integrations")}</h2>
            <p style={{ color: "var(--subtitle-text-color)", fontSize: 13, marginBottom: 20 }}>
                MobiSupport mevcut sistemlerinizle konuşur — kanallar, otomasyon, ERP ve CRM bağlantıları.
            </p>

            {INTEGRATION_CATEGORIES.map(cat => {
                const list = items.filter(i => i.category === cat);
                if (!list.length) return null;
                return (
                    <div key={cat} style={{ marginBottom: 24 }}>
                        <div className="text-xs font-semibold uppercase" style={{ letterSpacing: "0.04em", color: "var(--subtitle-text-color)", marginBottom: 10 }}>{cat}</div>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 14 }}>
                            {list.map(i => (
                                <IntegrationCard
                                    key={i.id}
                                    item={i}
                                    onToggle={() => setStatus(i.id, i.status === "connected" ? "disconnected" : "connected")}
                                    onConfig={() => setConfigOf(i)}
                                    onTest={() => test(i)}
                                    onLogs={() => setLogsOf(i)}
                                />
                            ))}
                        </div>
                    </div>
                );
            })}

            {configOf && (
                <ConfigModal
                    item={configOf}
                    onClose={() => setConfigOf(null)}
                    onSaved={() => { setStatus(configOf.id, "connected"); setConfigOf(null); notify(`${configOf.name}: ayarlar kaydedildi`, "success", 2000); }}
                />
            )}
            {logsOf && <LogsModal item={logsOf} onClose={() => setLogsOf(null)} />}
        </div>
    );
}

// ── Kart ────────────────────────────────────────────────────────────────────
function IntegrationCard({ item, onToggle, onConfig, onTest, onLogs }: {
    item: Integration; onToggle: () => void; onConfig: () => void; onTest: () => void; onLogs: () => void;
}) {
    const connected = item.status === "connected";
    return (
        <div style={{ background: "var(--base-bg)", border: "1px solid var(--border-color)", borderRadius: 12, padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontWeight: 700 }}>{item.name}</span>
                        <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 4, background: "var(--base-bg-darken-4)", color: "var(--subtitle-text-color)" }}>{item.phase}</span>
                    </div>
                    <div className="text-xs" style={{ color: "var(--subtitle-text-color)", marginTop: 2 }}>{item.desc}</div>
                </div>
                <span style={{
                    flexShrink: 0, fontSize: 11, fontWeight: 600, padding: "2px 10px", borderRadius: 999,
                    background: connected ? "#10B98122" : "#9CA3AF22", color: connected ? "#10B981" : "#9CA3AF",
                }}>{connected ? "Bağlı" : "Bağlı Değil"}</span>
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <button type="button" onClick={onToggle} style={connected ? ghostBtn : primaryBtn}>{connected ? "Bağlantıyı Kes" : "Bağlan"}</button>
                <button type="button" onClick={onConfig} style={ghostBtn}>Yapılandır</button>
                <button type="button" onClick={onTest} style={ghostBtn} disabled={!connected}>Test Et</button>
                <button type="button" onClick={onLogs} style={ghostBtn}>Loglar</button>
            </div>
        </div>
    );
}

// ── Yapılandır modal ───────────────────────────────────────────────────────────
function ConfigModal({ item, onClose, onSaved }: { item: Integration; onClose: () => void; onSaved: () => void }) {
    const [values, setValues] = useState<Record<string, string>>({});
    useEffect(() => {
        const init: Record<string, string> = {};
        item.fields.forEach(f => { init[f.key] = f.type === "select" ? (f.options?.[0] ?? "") : ""; });
        setValues(init);
    }, [item]);

    return (
        <Modal title={`${item.name} — Yapılandır`} onClose={onClose}>
            {item.kind === "apikeys" ? <ApiKeysManager /> : (
                <>
                    {item.fields.map(f => (
                        <div key={f.key} style={{ marginBottom: 12 }}>
                            <label className="block text-xs" style={{ marginBottom: 4, color: "var(--subtitle-text-color)" }}>{f.label}</label>
                            {f.type === "select" ? (
                                <select style={field} value={values[f.key] ?? ""} onChange={e => setValues(v => ({ ...v, [f.key]: e.target.value }))}>
                                    {f.options?.map(o => <option key={o} value={o}>{o}</option>)}
                                </select>
                            ) : (
                                <input style={field} type={f.type === "password" ? "password" : "text"} placeholder={f.placeholder} value={values[f.key] ?? ""} onChange={e => setValues(v => ({ ...v, [f.key]: e.target.value }))} />
                            )}
                        </div>
                    ))}
                </>
            )}
            <div style={{ display: "flex", gap: 8, marginTop: 8, justifyContent: "flex-end" }}>
                {item.kind !== "apikeys" && <button style={ghostBtn} onClick={() => notify(`${item.name}: test başarılı ✓`, "success", 1500)}>Test Et</button>}
                <button style={primaryBtn} onClick={onSaved}>Kaydet</button>
                <button style={ghostBtn} onClick={onClose}>İptal</button>
            </div>
        </Modal>
    );
}

// ── API Anahtarları yöneticisi ──────────────────────────────────────────────
function ApiKeysManager() {
    const [keys, setKeys] = useState<ApiKey[]>(() => [...MOCK_API_KEYS]);
    const [name, setName] = useState("");
    const [scope, setScope] = useState<ApiKey["scope"]>("Read");

    const add = () => {
        if (!name.trim()) return;
        const id = `k-${Date.now().toString(36)}`;
        setKeys(ks => [...ks, { id, name: name.trim(), scope, token: `mobi_live_${Math.abs(name.length * 7777).toString(16)}…${id.slice(-4)}` }]);
        setName("");
    };

    return (
        <div>
            <div style={{ background: "var(--base-bg-darken-4)", borderRadius: 8, overflow: "hidden", marginBottom: 12 }}>
                {keys.map((k, i) => (
                    <div key={k.id} className="flex items-center gap-3" style={{ padding: "10px 12px", borderTop: i === 0 ? "none" : "1px solid var(--border-color)" }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: 13 }}>{k.name}</div>
                            <code style={{ fontSize: 11, color: "var(--subtitle-text-color)" }}>{k.token}</code>
                        </div>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 6, background: "var(--base-bg)", border: "1px solid var(--border-color)" }}>{k.scope}</span>
                        <button style={{ ...ghostBtn, color: "#EF4444", borderColor: "#EF444455" }} onClick={() => setKeys(ks => ks.filter(x => x.id !== k.id))}>Sil</button>
                    </div>
                ))}
            </div>
            <div className="flex items-end gap-2">
                <div style={{ flex: 1 }}>
                    <label className="block text-xs" style={{ marginBottom: 4, color: "var(--subtitle-text-color)" }}>Anahtar Adı</label>
                    <input style={field} value={name} onChange={e => setName(e.target.value)} placeholder="örn. Mobil Uygulama" />
                </div>
                <div>
                    <label className="block text-xs" style={{ marginBottom: 4, color: "var(--subtitle-text-color)" }}>Yetki</label>
                    <select style={{ ...field, width: 120 }} value={scope} onChange={e => setScope(e.target.value as ApiKey["scope"])}>
                        <option>Read</option><option>Write</option><option>Admin</option>
                    </select>
                </div>
                <button style={primaryBtn} onClick={add}>+ Yeni Anahtar</button>
            </div>
        </div>
    );
}

// ── Loglar modal ─────────────────────────────────────────────────────────────
function LogsModal({ item, onClose }: { item: Integration; onClose: () => void }) {
    const logs = mockLogs(item.name);
    const color = (l: string) => l === "ok" ? "#10B981" : l === "warn" ? "#F59E0B" : "#EF4444";
    return (
        <Modal title={`${item.name} — Loglar`} onClose={onClose}>
            <div style={{ fontFamily: "monospace", fontSize: 12.5 }}>
                {logs.map((l, i) => (
                    <div key={i} className="flex items-center gap-3" style={{ padding: "8px 0", borderBottom: "1px solid var(--border-color)" }}>
                        <span style={{ color: "var(--subtitle-text-color)" }}>{l.time}</span>
                        <span style={{ width: 8, height: 8, borderRadius: "50%", background: color(l.level), flexShrink: 0 }} />
                        <span>{l.text}</span>
                    </div>
                ))}
            </div>
        </Modal>
    );
}

// ── Ortak modal ────────────────────────────────────────────────────────────────
function Modal({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
    return (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={onClose}>
            <div onClick={e => e.stopPropagation()} style={{ width: 520, maxWidth: "100%", maxHeight: "85vh", overflowY: "auto", background: "var(--base-bg)", border: "1px solid var(--border-color)", borderRadius: 14, padding: 20, color: "var(--base-text-color)" }}>
                <div className="flex items-center justify-between" style={{ marginBottom: 16 }}>
                    <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>{title}</h3>
                    <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "var(--subtitle-text-color)" }}>×</button>
                </div>
                {children}
            </div>
        </div>
    );
}

const primaryBtn: React.CSSProperties = { padding: "7px 14px", borderRadius: 8, fontSize: 13, fontWeight: 600, background: "var(--accent-color, #6366F1)", color: "#fff", border: "none", cursor: "pointer" };
const ghostBtn: React.CSSProperties = { padding: "7px 12px", borderRadius: 8, fontSize: 13, fontWeight: 500, background: "var(--base-bg)", color: "var(--base-text-color)", border: "1px solid var(--border-color)", cursor: "pointer" };
const field: React.CSSProperties = { width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid var(--border-color)", background: "var(--base-bg)", color: "var(--base-text-color)", fontSize: 13, outline: "none", boxSizing: "border-box" };
